/*©**********************************************************************************
*                                                                                   *
* SPDX-License-Identifier: AGPL-3.0-or-later                                        *
*                                                                                   *
* This program is licensed under the GNU Affero General Public License v3+.         *
* See the LICENSE file or <https://www.gnu.org/licenses/agpl-3.0.html> for details. *
*                                                                                   *
**********************************************************************************©*/

import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import MainLayout from './components/layouts/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { useDevMode } from './context/DevModeContext';

// Authentication pages (not lazy-loaded for faster initial access)
import Login from './pages/authentication/Login';
import Registration from './pages/authentication/Registration';

// Lazy-loaded pages to reduce initial bundle size
const Home = lazy(() => import('./pages/Home'));
const Profile = lazy(() => import('./pages/Profile'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const DCATrading = lazy(() => import('./pages/DCATrading'));
const ManageBots = lazy(() => import('./pages/ManageBots'));
const ManualTrade = lazy(() => import('./pages/ManualTrade'));
const Test = lazy(() => import('./components/Test'));
const BotDetails = lazy(() => import('./pages/Botdetails'));
const GridBotDetails = lazy(() => import('./pages/GridBotDetails'));
const TrendTrading = lazy(() => import('./pages/TrendTrading'));
const ArbitrageDashboard = lazy(() => import('./pages/ArbitrageBots'));
const FrontBots = lazy(() => import('./pages/FrontBots'));
const SettingsPage = lazy(() => import('./pages/setting'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminSignupRequests = lazy(() => import('./pages/admin/SignupRequests'));
const AdminMembers = lazy(() => import('./pages/admin/Members'));
const AdminMemberAccountView = lazy(() => import('./pages/admin/MemberAccountView'));
const AdminGlobalSettings = lazy(() => import('./pages/admin/GlobalSettings'));
const AdminSwapQueueMonitor = lazy(() => import('./pages/admin/SwapQueueMonitor'));
const NotFound = lazy(() => import('./pages/NotFound'));

const AppRouter = () => {
  const { pathname } = useLocation();
  const { devMode } = useDevMode();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);

  const allroutes = [
    { url: '/', component: Home },
    { url: '/profile', component: Profile },
    { url: '/portfolio', component: Portfolio, devOnly: true },
    { url: '/bots/dca', component: DCATrading },
    { url: '/bots/manage', component: ManageBots },
    { url: '/manual-trade', component: ManualTrade },
    { url: '/test', component: Test, devOnly: true },
    { url: '/bot-details/:id', component: BotDetails },
    { url: '/grid-bot-details/:id', component: GridBotDetails },
    { url: '/bots/trend', component: TrendTrading },
    { url: '/bots/arbitrage', component: ArbitrageDashboard, devOnly: true },
    { url: '/bots/frontrunner', component: FrontBots, devOnly: true },
    { url: '/settings', component: SettingsPage },
  ];

  const filteredRoutes = allroutes.filter((route) => devMode || !route.devOnly);

  const LoaderFallback = (
    <Center style={{ paddingTop: '10vh' }}>
      <Loader size="lg" />
    </Center>
  );

  return (
    <Suspense fallback={LoaderFallback}>
      <Routes>
        {/* Public Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Registration />} />
        
        {/* Protected Routes */}
        <Route element={<MainLayout />}>
          {filteredRoutes.map(({ url, component: Component }, i) => (
            <Route
              key={i}
              path={url}
              element={
                <ProtectedRoute>
                  <Component />
                </ProtectedRoute>
              }
            />
          ))}

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/signup-requests"
            element={
              <AdminRoute>
                <AdminSignupRequests />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members"
            element={
              <AdminRoute>
                <AdminMembers />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/:id/view"
            element={
              <AdminRoute>
                <AdminMemberAccountView />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings/global"
            element={
              <AdminRoute>
                <AdminGlobalSettings />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/swap-queue"
            element={
              <AdminRoute>
                <AdminSwapQueueMonitor />
              </AdminRoute>
            }
          />
        </Route>

        {/* 404 outside layout for cleaner page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
